import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Input,
  Spin,
  Empty,
  Alert,
  message,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import { useNotebooks, useNotesList, getDefaultNotebookId } from '../hooks/useNotes';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useNoteSearch } from '../hooks/useNoteSearch';

const { Title, Text, Paragraph } = Typography;

function stripMarkdown(md: string): string {
  return md
    .replace(/[#*_~`>\[\]()!|-]/g, '')
    .replace(/\n+/g, ' ')
    .trim();
}

export default function NotesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { notebooks, isLoading: notebooksLoading } = useNotebooks();
  const { notes, isLoading: notesLoading, createNote } = useNotesList(undefined, false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterNotebookId, setFilterNotebookId] = useState<number | null>(null);

  const isSearching = searchQuery.trim().length >= 2;
  const { results: searchResults, isLoading: searchLoading } = useNoteSearch(searchQuery);

  if (user?.role !== 'author') {
    return <Alert type="error" message="Access denied. Author only." showIcon />;
  }

  const notebookMap = useMemo(() => {
    const map: Record<number, string> = {};
    notebooks.forEach((nb) => { map[nb.id] = nb.name; });
    return map;
  }, [notebooks]);

  const groupedNotes = useMemo(() => {
    const filtered = filterNotebookId
      ? notes.filter((n) => n.notebookId === filterNotebookId)
      : notes;
    const groups: Record<number, typeof notes> = {};
    filtered.forEach((note) => {
      if (!groups[note.notebookId]) groups[note.notebookId] = [];
      groups[note.notebookId]!.push(note);
    });
    return Object.entries(groups).map(([id, groupNotes]) => ({
      notebookId: Number(id),
      name: notebookMap[Number(id)] || 'Unknown',
      notes: groupNotes,
    }));
  }, [notes, notebookMap, filterNotebookId]);

  async function handleNewNote(notebookId: number) {
    try {
      const note = await createNote.mutateAsync({ title: 'Untitled', content: '', notebookId });
      navigate(`/reviewer/notes/${note.id}`);
    } catch {
      message.error('Failed to create note');
    }
  }

  const loading = notebooksLoading || notesLoading;

  // Desktop: sidebar is primary navigation, show simple empty state
  if (isDesktop) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <Empty
          description={
            <span style={{ color: 'var(--text-muted)' }}>
              {notebooks.length === 0
                ? 'Create a notebook in the sidebar to get started'
                : 'Select a note from the sidebar, or create a new one'}
            </span>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // Mobile: full note list with search and filter
  return (
    <div>
      <Title level={2} style={{ margin: '0 0 16px', color: '#f5f5f5' }}>Notes</Title>

      {/* Search */}
      <Input
        placeholder="Search notes..."
        prefix={<SearchOutlined style={{ color: 'var(--text-muted)' }} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        allowClear
        style={{ marginBottom: 12 }}
      />

      {/* Notebook filter chips */}
      {notebooks.length > 1 && !isSearching && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <Button
            size="small"
            type={filterNotebookId === null ? 'primary' : 'default'}
            onClick={() => setFilterNotebookId(null)}
            style={filterNotebookId === null ? {} : { color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
          >
            All
          </Button>
          {notebooks.map((nb) => (
            <Button
              key={nb.id}
              size="small"
              type={filterNotebookId === nb.id ? 'primary' : 'default'}
              onClick={() => setFilterNotebookId(filterNotebookId === nb.id ? null : nb.id)}
              style={filterNotebookId === nb.id ? {} : { color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
            >
              {nb.name}
            </Button>
          ))}
        </div>
      )}

      {/* Search results */}
      {isSearching ? (
        searchLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
        ) : searchResults.length === 0 ? (
          <Empty description="No results" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          searchResults.map((r) => (
            <Link key={r.note.id} to={`/reviewer/notes/${r.note.id}`} style={{ display: 'block', textDecoration: 'none' }}>
              <div
                style={{
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span
                    style={{ color: '#f0f0f0', fontSize: 15, fontWeight: 600 }}
                    // biome-ignore lint: search highlight snippets from our own API
                    dangerouslySetInnerHTML={{ __html: r.titleSnippet }}
                  />
                  <Text type="secondary" style={{ fontSize: 11, flexShrink: 0, marginLeft: 12 }}>
                    {r.notebookName}
                  </Text>
                </div>
                {r.contentSnippet && (
                  <div
                    style={{ color: '#888', fontSize: 13, margin: '4px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    // biome-ignore lint: search highlight snippets from our own API
                    dangerouslySetInnerHTML={{ __html: r.contentSnippet }}
                  />
                )}
              </div>
            </Link>
          ))
        )
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
      ) : notes.length === 0 ? (
        <Empty
          description={notebooks.length === 0 ? 'Create a notebook to get started' : 'No notes yet'}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        groupedNotes.map((group) => (
          <div key={group.notebookId} style={{ marginBottom: 32 }}>
            {/* Only show notebook headers when not filtering to a single notebook */}
            {filterNotebookId === null && (
              <Text
                strong
                style={{ color: 'var(--text-secondary)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}
              >
                {group.name}
              </Text>
            )}
            {group.notes.map((note) => (
              <Link key={note.id} to={`/reviewer/notes/${note.id}`} style={{ display: 'block', textDecoration: 'none' }}>
                <div
                  style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text strong style={{ color: '#f0f0f0', fontSize: 15 }}>
                      {note.title || 'Untitled'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12, flexShrink: 0, marginLeft: 12 }}>
                      {formatDistanceToNow(new Date(note.updatedAt), { addSuffix: true })}
                    </Text>
                  </div>
                  {note.content && (
                    <Paragraph
                      ellipsis={{ rows: 1 }}
                      style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}
                    >
                      {stripMarkdown(note.content).slice(0, 120)}
                    </Paragraph>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ))
      )}

      {/* Spacer so last note isn't hidden behind FAB */}
      <div style={{ height: 80 }} />

      {/* Mobile FAB */}
      <Button
        type="primary"
        shape="circle"
        icon={<PlusOutlined />}
        size="large"
        onClick={() => {
          const defaultId = getDefaultNotebookId(notebooks);
          if (defaultId) handleNewNote(defaultId);
        }}
        loading={createNote.isPending}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          zIndex: 100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}
      />
    </div>
  );
}
